declare const window: any;

const CORDOVA_BRIDGE_ERROR_PATTERNS = [
  /java object is gone/i,
  /error invoking exec/i,
  /_cordovaNative/i,
  /exec\(\) called without bridgeSecret/i,
];

function getErrorText(error: any): string {
  if (error === undefined || error === null) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  const parts: string[] = [];

  if (typeof error.message === "string") {
    parts.push(error.message);
  }
  if (typeof error.stack === "string") {
    parts.push(error.stack);
  }
  if (typeof error.toString === "function") {
    parts.push(error.toString());
  }

  return parts.join("\n");
}

export function isCordovaNativeBridgeError(error: any): boolean {
  const errorText = getErrorText(error);
  return CORDOVA_BRIDGE_ERROR_PATTERNS.some((pattern) => pattern.test(errorText));
}

function isAndroidCordova(): boolean {
  return typeof window !== "undefined"
    && !!window.cordova
    && window.cordova.platformId === "android";
}

export function preferPromptCordovaBridge(reason: string): boolean {
  if (!isAndroidCordova()) {
    return false;
  }

  try {
    const cordovaRequire = window.cordova && window.cordova.require;
    const exec = typeof cordovaRequire === "function" ? cordovaRequire("cordova/exec") : undefined;
    const promptMode = exec && exec.jsToNativeModes && exec.jsToNativeModes.PROMPT;

    if (exec && typeof exec.setJsToNativeBridgeMode === "function" && promptMode !== undefined) {
      exec.setJsToNativeBridgeMode(promptMode);
      console.warn(`Using Cordova prompt bridge after native bridge issue: ${reason}`);
      return true;
    }
  } catch (error) {
    console.warn("Could not switch Cordova bridge mode", error);
  }

  return false;
}

function prepareCordovaPluginCall(label: string): boolean {
  if (typeof window === "undefined" || !window.cordova) {
    return false;
  }

  if (isAndroidCordova() && !window._cordovaNative) {
    preferPromptCordovaBridge(`${label} preflight found no _cordovaNative object`);
  }

  return true;
}

export function runCordovaPluginCall<T>(
  label: string,
  call: () => T,
  fallback?: T,
  options: { retryOnBridgeError?: boolean } = {}
): T | undefined {
  if (!prepareCordovaPluginCall(label)) {
    return fallback;
  }

  try {
    return call();
  } catch (error) {
    if (!isCordovaNativeBridgeError(error)) {
      throw error;
    }

    const promptBridgeAvailable = preferPromptCordovaBridge(label);
    // Some plugins mutate their JS state before calling native code and cannot
    // be invoked twice. Still switch the bridge for subsequent plugin calls.
    if (promptBridgeAvailable && options.retryOnBridgeError !== false) {
      try {
        return call();
      } catch (retryError) {
        if (!isCordovaNativeBridgeError(retryError)) {
          throw retryError;
        }
        console.warn(`Skipping Cordova plugin call after bridge retry failed: ${label}`, retryError);
        return fallback;
      }
    }

    console.warn(`Skipping Cordova plugin call because the native bridge is unavailable: ${label}`, error);
    return fallback;
  }
}

export function runCordovaPluginPromise<T>(
  label: string,
  call: () => Promise<T>
): Promise<T | undefined> {
  const firstAttempt = runCordovaPluginCall<Promise<T>>(label, call);

  if (firstAttempt === undefined) {
    return Promise.resolve(undefined);
  }

  return firstAttempt.catch((error: any) => {
    if (!isCordovaNativeBridgeError(error)) {
      throw error;
    }

    if (!preferPromptCordovaBridge(label)) {
      console.warn(`Skipping Cordova plugin promise because the native bridge is unavailable: ${label}`, error);
      return undefined;
    }

    const retryAttempt = runCordovaPluginCall<Promise<T>>(label, call);
    if (retryAttempt === undefined) {
      return undefined;
    }

    return retryAttempt.catch((retryError: any) => {
      if (!isCordovaNativeBridgeError(retryError)) {
        throw retryError;
      }
      console.warn(`Skipping Cordova plugin promise after bridge retry failed: ${label}`, retryError);
      return undefined;
    });
  });
}



// WEBPACK FOOTER //
// ./src/helpers/cordovaBridge.ts